import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import type { GraphData } from '../../api/process-client/process-client.type';
import type { ResolvedPhaseLabels } from '../../helpers/phase-labels/phase-labels.type';
import { PhaseKey } from '../../helpers/phase-labels/phase-labels.type';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';

type LegendItem = {
  icon: string;
  color: string;
  text: string;
  variant?: 'filled' | 'outlined';
};

type Props = {
  legendItems: LegendItem[];
  graphData?: GraphData;
  showOverview?: boolean;
  /**
   * Optional per-workflow display labels. When supplied, the
   * "Waiting" overview metric label is replaced with the override for
   * the canonical `blocked` key.
   */
  resolvedLabels?: ResolvedPhaseLabels;
};

export const ProcessGraphLegend = ({ legendItems, graphData, showOverview = false, resolvedLabels }: Props) => {
  const i18n = useTranslation();
  const [isLegendOpen, { toggle: toggleLegend }] = useDisclosure(false);
  const [isOverviewOpen, { toggle: toggleOverview }] = useDisclosure(false);

  const workUnitsName = i18n.t('common.work_units');
  const waitingLabel = i18n.t('common.workflow_steps.waiting');
  const blockedLabel = resolvedLabels?.[PhaseKey.Blocked]?.trim() || waitingLabel;

  const overviewMetrics = [
    {
      key: 'total',
      label: `Total ${workUnitsName}`,
      value: `${graphData?.task_count || 0}`,
    },
    {
      key: 'out-of-workflow',
      label: 'Out of Workflow',
      value: `${graphData?.metrics?.skipped_tasks || 0}%`,
    },
    {
      key: 'backwards-steps',
      label: 'Backwards Steps',
      value: `${graphData?.metrics?.backwards_tasks || 0}%`,
    },
    {
      key: 'blocked',
      label: blockedLabel,
      value: `${graphData?.metrics?.blocked_tasks || 0}%`,
    },
  ];

  return (
    <LegendContainer>
      {showOverview && (
        <>
          <Group justify="space-between">
            <Text size="small" weight="bold">
              Overview
            </Text>
            <Icon name={isOverviewOpen ? 'expand_more' : 'expand_less'} size={20} onClick={toggleOverview} />
          </Group>
          <CollapsibleContent isOpen={isOverviewOpen}>
            <Divider style={{ margin: '8px 0' }} />
            <Stack gap="xs">
              {overviewMetrics.map((metric) => (
                <Group justify="space-between" key={metric.key}>
                  <Text size="tiny">{metric.label}</Text>
                  <Text size="tiny" weight="bold">
                    {metric.value}
                  </Text>
                </Group>
              ))}
            </Stack>
          </CollapsibleContent>
          <Divider style={{ margin: '8px 0' }} />
        </>
      )}
      <Group justify="space-between">
        <Text size="small" weight="bold">
          Legend
        </Text>
        <Icon name={isLegendOpen ? 'expand_more' : 'expand_less'} size={20} onClick={toggleLegend} />
      </Group>
      <CollapsibleContent isOpen={isLegendOpen}>
        <Divider style={{ margin: '8px 0' }} />
        <Stack gap="xs">
          {legendItems.map((item, index) => (
            <Group gap="xs" key={index}>
              <Icon name={item.icon} variant={item.variant ?? 'outlined'} size={20} color={item.color} />
              <Text size="tiny">{item.text}</Text>
            </Group>
          ))}
        </Stack>
      </CollapsibleContent>
    </LegendContainer>
  );
};

const LegendContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 5;
  max-width: 200px;
  width: 100%;
  position: absolute;
  bottom: 15px;
  left: 2px;
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  max-height: ${(props) => (props.isOpen ? '200px' : '0')};
  opacity: ${(props) => (props.isOpen ? '1' : '0')};
  overflow: hidden;
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
`;
